'use strict';

const _ = require('lodash');
const { OPTIONS } = require('../../constants');

const DISABLED = ['停用', 'disabled', '停用'];

/**
 * @param {string} key
 * @returns {object}
 */
const getOptions = key => OPTIONS[key];

/**
 * @param {string} key
 * @returns {string[]}
 */
const getKeys = key => OPTIONS[key + '._sort'] || _.keys(OPTIONS[key]);

/**
 * @param {string} key
 * @param {number} localeIdx
 * @returns {undefined|object|array}
 */
const getLocaledOptions = (key, localeIdx) => {
	const _opts = OPTIONS[key];
	if (_.isEmpty(_opts)) return undefined;

	const opts = _.cloneDeep(_opts);
	const idxs = OPTIONS[key + '._sort'];
	if (idxs) return idxs.map(id => ({id, name: opts[id][localeIdx]}));
	return _.transform(opts, (result, value, key) => result[key] = value[localeIdx]);
};

const getTreeOptions = (key, localeIdx) => {
	const opts = OPTIONS[key];
	if (_.isEmpty(opts)) return undefined;

	const fn = src => _.transform(src, (result, obj, idx) => {
		obj.name = obj.name[localeIdx];
		if(_.isArray(obj.children)) obj.children = fn(obj.children);
		result.push(obj);
		return result;
	}, []);
	return fn(_.cloneDeep(opts));
};

/**
 * 取得單一的 label
 * @param {string} key
 * @param {*} value 一個值
 * @param {number} localeIdx
 * @param {array|object} [opts] 如果有事先準備好的資料的話，可以傳入使用
 * @returns {string}
 */
const getLabel = (key, value, localeIdx, opts) => {
	if (!opts)
		opts = getLocaledOptions(key, localeIdx);

	if (!opts) return '';
	if (Array.isArray(opts)) {
		const { name } = opts.find(p => p.id == value) || 0;
		return name || '';
	}
	return opts.hasOwnProperty(value) ? opts[value] : '';
};

const getLabelWithTemplate = (key, value, localeIdx, replaceKeys = {}, opts) => {
	const returnValue = getLabel(key, value, localeIdx, opts);
	if (Object.keys(replaceKeys).length === 0) return returnValue;

	try {
		return _.template(returnValue)(replaceKeys);
	} catch (error) {
		console.log('getLabelWithTemplate error:', key, value, error);
		return returnValue;
	};
};

/**
 * 取得單一的 label
 * @param {string} key
 * @param {*} value 一個值
 * @param {number} localeIdx
 * @param {array|object} [opts] 如果有事先準備好的資料的話，可以傳入使用
 * @returns {string}
 */
const getLabelAndUseTemplate = (key, value, localeIdx, replaceKeys = {}, opts) => {
	if (!opts)
		opts = getLocaledOptions(key, localeIdx);

	if (!opts) return '';
	if (Array.isArray(opts)) {
		const { name } = opts.find(p => p.id == value) || 0;
		return name || '';
	}
	const returnValue = opts.hasOwnProperty(value) ? opts[value] : '';
	if (Object.keys(replaceKeys).length === 0) return returnValue;

	try {
		return _.template(returnValue)(replaceKeys);
	} catch (error) {
		console.log('getLabelAndUseTemplate error:', key, value, error);
		return returnValue;
	}
};

/**
 * 取得多個 label，用 delimiter 做區隔
 * @param {string} key
 * @param {*[]|string} values 多個值 (array 或 delimiter 分隔的字串)
 * @param {number} localeIdx
 * @param {string} [delimiter] 輸出時，用來區隔每個 label 的字串 (預設是半形逗號，前後無空白)
 * @returns {string}
 */
const getJoinedLabels = (key, values, localeIdx, delimiter = ',') => {
	const opts = getLocaledOptions(key, localeIdx);
	values = _.isArray(values)
		? values
		: _.isString(values) && values.length !== 0
			? values.split(',')
			: values;
	if (!_.isArray(values)) return getLabel(key, values, localeIdx);
	const out = [];
	values.forEach(value => {
		out.push(getLabel(key, value, localeIdx, opts));
	});
	if (out.length === 0) return undefined;
	return out.join(delimiter);
};

/**
 * 排除已停用的 options
 * <p>使用時機：ex: 新增時</p>
 * @param {object[]} options
 * @returns {object[]}
 */
const filterEnable = options => {
	return options.filter(({ active, id }) => active);
};

/**
 * 排除已停用的 options，除了 excludeIds 用到的項目。<br/>
 * 並把 excludeIds 用到的項目加上 '(已停用)' 字樣
 * <p>使用時機：ex: 編輯時</p>
 * @param {object[]} options
 * @param {*} excludeIds
 * @param {number} localeIdx
 * @returns {object[]}
 */
const fixDisable = (options, excludeIds, localeIdx) => {
	if (!_.isArray(excludeIds)) excludeIds = [excludeIds];

	//只顯示啟用與舊的選項
	options = options.filter(({ active, id }) => active || excludeIds.some(xid => xid == id));

	//加上停用文字
	const disabled = ` (${DISABLED[localeIdx]})`;
	return _.map(options, item => {
		let clone = _.clone(item);
		if (!clone.active) clone.name += disabled;
		return clone;
	});
};

/**
 * 啟用&關閉(已停用) (ex:查詢)
 */
const adaptDisable = (options, localeIdx) => {
	//加上停用文字
	const disabled = ` (${DISABLED[localeIdx]})`;
	return _.map(options, item => {
		let clone = _.clone(item);
		if (!clone.active) clone.name += disabled;
		return clone;
	});
};

/**
 * service-excelImport type:'map' 配套工具，產生反向 map 的 Object
 * @param {*} options 用來檢查 mapping 的資料 (Array | Object)
 * @param {*} opts 轉換用參數
 * @returns {*} 必定是 {value1: key1, value2: key2,...}
 */
const mapOptions = (options, { mapKey } = 0) => Array.isArray(options)
	? options.reduce((p1, c1) => {
		p1[c1.name] = c1.id;
		return p1;
	}, {})
	: Object.keys(options).reduce((p2, c2) => {
		if (mapKey && !p2[c2]) p2[c2] = c2;
		p2[options[c2]] = c2;
		return p2;
	}, {});

/**
 * mapOptions 加強版自動工具
 * @param {*} src 用來檢查 mapping 的參考 (String | Array | Object)
 * @param {*} opts 轉換用參數
 * @returns {*} 必定是 {value1: key1, value2: key2,...}
 */
const mapOptAuto = (src, opts) => {
	const { localeIdx, mapKey, exts } = opts || 0;
	if (typeof (src) === 'string') src = getLocaledOptions(src, localeIdx);
	if (Array.isArray(src)) {
		src = [...src, ...exts || []];
		if (src[0]) {
			src = [ //注意優先權
				...mapKey ? src.map(({ id }) => ({ id, name: id })) : [],
				...src[0].code ? src.map(({ id, code }) => ({ id, name: code })) : [],
				...src
			];
		}
	} else Object.assign(src || 0, exts);

	return mapOptions(src || 0, opts);
};

/**
 * 取得 nested option path 的原始值
 * @param {string} optionsKey
 * @param {...(string|number)} path
 * @returns {*|undefined}
 * @example getOptionPath('taipeiMetroMile', 21, 81);
 */
const getOptionPath = (optionsKey, ...path) => {
	let node = OPTIONS[optionsKey];
	for (const p of path) {
		if (node == null) return undefined;
		node = node[p];
	}
	return node;
};

module.exports = {
	getOptions, getKeys, getLocaledOptions, getTreeOptions, getLabel, getLabelWithTemplate, getJoinedLabels,
	filterEnable, fixDisable, adaptDisable, mapOptions, mapOptAuto, getLabelAndUseTemplate,
	getOptionPath,
};
